/**
 * SSO sign-in for the prejoin screen: the OIDC Authorization Code flow with PKCE (RFC 7636)
 * against the Keycloak realm of the stand. The SPA is a public client, so the code-to-token
 * exchange happens in the browser (fetch to the realm's token endpoint; Keycloak answers the
 * CORS preflight for the configured webOrigins). The token pair is kept in web storage, never
 * in a URL after the callback is consumed, never logged. The session (`vc.ssoSession`) lives
 * in localStorage and is shared by every tab of the origin: tabs see each other's rotations,
 * and the refresh POST is single-flighted and serialized across tabs (Web Locks) - the realm
 * rotates refresh tokens (revokeRefreshToken=true, maxReuse=0), so a token POSTed twice would
 * revoke the whole chain and hard-log the user out. The PKCE flow state (`vc.ssoFlow`) stays
 * per-tab in sessionStorage. The localStorage trade-off is accepted deliberately: the pair
 * survives a browser restart, but the refresh token dies with the realm's 10-minute idle SSO
 * session, so a stolen persisted token is short-lived.
 *
 * On top of the flow primitives this module owns the session lifecycle: refreshSession-style
 * renewal (refreshSsoSession), a near-expiry auto-refresh scheduler (startSsoAutoRefresh) and
 * the 401/sso_invalid recovery wrapper for API calls (callWithSsoRecovery). While a tab stays
 * open the scheduler renews the session ~4 minutes in, which also keeps the realm's SSO
 * session (10 min idle) alive - no re-login for the user.
 *
 * Everything here is pure or takes injectable dependencies (storage, fetch, redirect target,
 * clock, timers), so the flow logic is unit-tested without touching real browser state.
 */

import type { KeyValueStorage } from '../room/devicePrefs';

/**
 * Keycloak deployment the SPA talks to. Explicit VITE_ variables (documented in web/README.md);
 * the defaults match the stand realm bootstrapped by `just up` (docs/contract.md ports).
 */
export interface SsoConfig {
  /** Keycloak base URL, e.g. http://127.0.0.1:9081. */
  readonly baseUrl: string;
  /** Realm name, e.g. video-conf. */
  readonly realm: string;
  /** Public client id, e.g. video-conf-web. */
  readonly clientId: string;
}

/** The stand realm: issuer http://127.0.0.1:9081/realms/video-conf, public client video-conf-web. */
export const DEFAULT_SSO_CONFIG: SsoConfig = {
  baseUrl: 'http://127.0.0.1:9081',
  realm: 'video-conf',
  clientId: 'video-conf-web',
};

/**
 * Resolves the config from the build environment with the stand defaults underneath. The values
 * are plain strings: a non-empty VITE_ override wins, anything else falls back per field.
 */
export function ssoConfigFromEnv(
  env: {
    readonly VITE_KEYCLOAK_URL?: unknown;
    readonly VITE_KEYCLOAK_REALM?: unknown;
    readonly VITE_KEYCLOAK_CLIENT_ID?: unknown;
  } = import.meta.env,
): SsoConfig {
  const read = (value: unknown, fallback: string): string =>
    typeof value === 'string' && value.trim().length > 0 ? value.trim() : fallback;
  return {
    baseUrl: read(env.VITE_KEYCLOAK_URL, DEFAULT_SSO_CONFIG.baseUrl).replace(/\/+$/, ''),
    realm: read(env.VITE_KEYCLOAK_REALM, DEFAULT_SSO_CONFIG.realm),
    clientId: read(env.VITE_KEYCLOAK_CLIENT_ID, DEFAULT_SSO_CONFIG.clientId),
  };
}

/** The realm's OIDC authorization endpoint (standard flow, PKCE required by the client policy). */
export function authorizeEndpoint(config: SsoConfig): string {
  return `${config.baseUrl}/realms/${config.realm}/protocol/openid-connect/auth`;
}

/** The realm's OIDC token endpoint; supports CORS for the SPA origins (stand webOrigins). */
export function tokenEndpoint(config: SsoConfig): string {
  return `${config.baseUrl}/realms/${config.realm}/protocol/openid-connect/token`;
}

/** Query marker identifying an SSO callback landing on the room URL. */
export const SSO_CALLBACK_MARKER = 'sso=callback';

// --- PKCE (RFC 7636) ----------------------------------------------------------------------

/** Byte length of the code_verifier pre-image: 32 bytes -> 43 base64url chars (the minimum). */
const VERIFIER_BYTES = 32;
/** Byte length of the anti-CSRF state token. */
const STATE_BYTES = 16;

/** Encodes bytes as base64url (RFC 4648 section 5) without padding. */
function base64urlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Decodes the base64url segment of a JWT (no padding); returns null on malformed input. */
function base64urlDecode(segment: string): string | null {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  try {
    return atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  } catch {
    return null;
  }
}

function randomBase64url(byteLength: number): string {
  const bytes = new Uint8Array(byteLength);
  globalThis.crypto.getRandomValues(bytes);
  return base64urlEncode(bytes);
}

/** Generates a fresh code_verifier: 32 random bytes, base64url (43 chars). */
export function generateCodeVerifier(): string {
  return randomBase64url(VERIFIER_BYTES);
}

/** Generates a random state token (anti-CSRF, round-tripped through sessionStorage). */
export function generateState(): string {
  return randomBase64url(STATE_BYTES);
}

/** S256 code challenge: BASE64URL(SHA256(verifier)), per RFC 7636 section 4.2. */
export async function codeChallengeS256(verifier: string): Promise<string> {
  const subtle = globalThis.crypto?.subtle;
  if (subtle === undefined) {
    throw new Error('WebCrypto is unavailable: cannot compute the PKCE code challenge');
  }
  const digest = await subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return base64urlEncode(new Uint8Array(digest));
}

// --- Flow state (sessionStorage) ------------------------------------------------------------

/** sessionStorage key of the in-flight sign-in (verifier + CSRF state + room). */
export const SSO_FLOW_STORAGE_KEY = 'vc.ssoFlow';
/** sessionStorage key of the signed-in session (token pair + expiry timestamps). */
export const SSO_SESSION_STORAGE_KEY = 'vc.ssoSession';

/** State of a sign-in that has been started but has not come back from Keycloak yet. */
export interface SsoFlowState {
  /** Room the sign-in was started from; the callback must return to it. */
  readonly room: string;
  readonly verifier: string;
  readonly state: string;
}

/**
 * The persisted sign-in session: the access token, the rotating refresh token and the expiry
 * timestamps (epoch ms). The refresh token is single-use in this realm - it is replaced on
 * every refresh and never leaves sessionStorage.
 */
export interface SsoSession {
  readonly accessToken: string;
  /** Rotating refresh token; absent when the provider answered without one. */
  readonly refreshToken?: string | undefined;
  /** Epoch ms when the access token expires (from expires_in, else the token's exp claim). */
  readonly accessExpiresAt: number;
  /** Epoch ms when the refresh token dies (the realm's SSO idle timeout); absent if unknown. */
  readonly refreshExpiresAt?: number | undefined;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readNonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function loadJson<T>(
  storage: KeyValueStorage,
  key: string,
  narrow: (value: Record<string, unknown>) => T | null,
): T | null {
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!isRecord(parsed)) {
    return null;
  }
  return narrow(parsed);
}

function saveJson(storage: KeyValueStorage, key: string, value: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    // Best-effort: a blocked storage only costs the sign-in convenience, never the guest path.
  }
}

function removeJson(storage: KeyValueStorage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    // Same best-effort contract as saveJson.
  }
}

/**
 * Loads the in-flight flow state. Anything unexpected - missing entry, invalid JSON, wrong
 * shape - yields null; the function never throws.
 */
export function loadSsoFlow(
  storage: KeyValueStorage,
  key: string = SSO_FLOW_STORAGE_KEY,
): SsoFlowState | null {
  return loadJson(storage, key, (parsed) => {
    const room = readNonEmpty(parsed.room);
    const verifier = readNonEmpty(parsed.verifier);
    const state = readNonEmpty(parsed.state);
    return room !== null && verifier !== null && state !== null ? { room, verifier, state } : null;
  });
}

/** Persists the in-flight flow state as JSON; storage failures are swallowed. */
export function saveSsoFlow(
  storage: KeyValueStorage,
  flow: SsoFlowState,
  key: string = SSO_FLOW_STORAGE_KEY,
): void {
  saveJson(storage, key, flow);
}

/** Clears the in-flight flow state. */
export function clearSsoFlow(storage: KeyValueStorage, key: string = SSO_FLOW_STORAGE_KEY): void {
  removeJson(storage, key);
}

/** Loads the signed-in session; corrupted, absent or legacy-shaped content yields null. */
export function loadSsoSession(
  storage: KeyValueStorage,
  key: string = SSO_SESSION_STORAGE_KEY,
): SsoSession | null {
  return loadJson(storage, key, (parsed) => {
    const accessToken = readNonEmpty(parsed.accessToken);
    const accessExpiresAt = isFiniteNumber(parsed.accessExpiresAt) ? parsed.accessExpiresAt : null;
    if (accessToken === null || accessExpiresAt === null) {
      return null;
    }
    const session: {
      accessToken: string;
      refreshToken?: string;
      accessExpiresAt: number;
      refreshExpiresAt?: number;
    } = { accessToken, accessExpiresAt };
    const refreshToken = readNonEmpty(parsed.refreshToken);
    if (refreshToken !== null) {
      session.refreshToken = refreshToken;
    }
    if (isFiniteNumber(parsed.refreshExpiresAt)) {
      session.refreshExpiresAt = parsed.refreshExpiresAt;
    }
    return session;
  });
}

/** Persists the signed-in session (token pair + expiries); storage failures are swallowed. */
export function saveSsoSession(
  storage: KeyValueStorage,
  session: SsoSession,
  key: string = SSO_SESSION_STORAGE_KEY,
): void {
  saveJson(storage, key, session);
}

/**
 * Clears the signed-in session. Sign-out (v1) revokes nothing server-side: the refresh token
 * stays valid in the realm until the SSO idle timeout expires it - it is useless without the
 * browser's storage, which is the accepted trade-off for not calling a logout endpoint. On the
 * shared store this signs out every tab of the origin, which is what a shared session means.
 */
export function clearSsoSession(
  storage: KeyValueStorage,
  key: string = SSO_SESSION_STORAGE_KEY,
): void {
  removeJson(storage, key);
}

/** Returns the tab-scoped sessionStorage when available, null where access throws. */
export function browserSessionStorage(): KeyValueStorage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const probeKey = 'vc.sso.probe';
    window.sessionStorage.setItem(probeKey, probeKey);
    window.sessionStorage.removeItem(probeKey);
    return window.sessionStorage;
  } catch {
    return null;
  }
}

// --- Session storage: one shared session across tabs ---------------------------------------------

/** Returns the origin-shared localStorage when available, null where access throws. */
export function browserLocalStorage(): KeyValueStorage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const probeKey = 'vc.sso.probe.local';
    window.localStorage.setItem(probeKey, probeKey);
    window.localStorage.removeItem(probeKey);
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Builds the browser storage of the SSO session. The token pair (`vc.ssoSession`) is routed to
 * the shared localStorage so every tab of the origin sees the same live session and a rotated
 * pair becomes immediately visible to the others; every other key (the per-tab PKCE flow state
 * `vc.ssoFlow`, the re-login loop guard) goes to the per-tab storage. Reading the session
 * migrates a legacy per-tab copy once, guarded in both directions: a session already present
 * in the shared storage always wins, and the per-tab copy is drained after the move so it can
 * never resurrect a signed-out session. Without localStorage the store degrades to the legacy
 * per-tab behavior; with both blocked it is null.
 */
export function createSsoSharedStorage(
  local: KeyValueStorage | null,
  perTab: KeyValueStorage | null,
): KeyValueStorage | null {
  if (local === null) {
    return perTab;
  }
  const fallback = perTab ?? local;
  return {
    getItem: (key: string): string | null => {
      if (key !== SSO_SESSION_STORAGE_KEY) {
        return fallback.getItem(key);
      }
      const shared = local.getItem(SSO_SESSION_STORAGE_KEY);
      if (shared !== null) {
        return shared;
      }
      const legacy = perTab?.getItem(SSO_SESSION_STORAGE_KEY) ?? null;
      if (legacy === null) {
        return null;
      }
      try {
        local.setItem(SSO_SESSION_STORAGE_KEY, legacy);
      } catch {
        // Shared storage refused the write: serve the legacy copy, retry the move next read.
        return legacy;
      }
      perTab?.removeItem(SSO_SESSION_STORAGE_KEY);
      return legacy;
    },
    setItem: (key: string, value: string): void => {
      if (key !== SSO_SESSION_STORAGE_KEY) {
        fallback.setItem(key, value);
        return;
      }
      local.setItem(SSO_SESSION_STORAGE_KEY, value);
    },
    removeItem: (key: string): void => {
      if (key !== SSO_SESSION_STORAGE_KEY) {
        fallback.removeItem(key);
        return;
      }
      local.removeItem(SSO_SESSION_STORAGE_KEY);
    },
  };
}

/**
 * Memoized browser store of the SSO session (see {@link createSsoSharedStorage}). One identity
 * per tab is load-bearing: the refresh single-flight dedupes on the storage object, so every
 * caller in the process must observe the same instance.
 */
let sharedSsoStorage: KeyValueStorage | null | undefined;

export function browserSsoStorage(): KeyValueStorage | null {
  if (sharedSsoStorage === undefined) {
    sharedSsoStorage = createSsoSharedStorage(browserLocalStorage(), browserSessionStorage());
  }
  return sharedSsoStorage;
}

// --- Sign-in start --------------------------------------------------------------------------

/** Minimal surface of Location the redirect needs; injectable for tests. */
export interface RedirectTarget {
  assign(href: string): void;
}

/** Inputs of one sign-in start: the room to come back to and the page origin. */
export interface SsoStartInput {
  readonly room: string;
  readonly origin: string;
}

/**
 * Starts the Authorization Code + PKCE sign-in: persists verifier, CSRF state and room in
 * sessionStorage, then redirects to the realm's authorize endpoint. The redirect_uri is the
 * room URL carrying the callback marker; Keycloak requires it to be registered verbatim.
 * Resolves with the authorize URL after the redirect has been issued.
 */
export async function startSsoSignIn(
  config: SsoConfig,
  input: SsoStartInput,
  storage: KeyValueStorage,
  target: RedirectTarget = window.location,
): Promise<string> {
  const verifier = generateCodeVerifier();
  const challenge = await codeChallengeS256(verifier);
  const state = generateState();
  const redirectUri = `${input.origin}/r/${input.room}?${SSO_CALLBACK_MARKER}`;
  saveSsoFlow(storage, { room: input.room, verifier, state });

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: config.clientId,
    redirect_uri: redirectUri,
    scope: 'openid',
    state,
    code_challenge: challenge,
    code_challenge_method: 'S256',
  });
  const href = `${authorizeEndpoint(config)}?${params.toString()}`;
  target.assign(href);
  return href;
}

// --- Callback handling ------------------------------------------------------------------------

/** What landed on the room URL. */
export type SsoCallbackParse =
  /** No callback marker in the query: a plain prejoin visit. */
  | { readonly kind: 'none' }
  | { readonly kind: 'callback'; readonly code: string; readonly state: string }
  | {
      readonly kind: 'provider_error';
      readonly error: string;
      readonly description: string | null;
    };

/**
 * Parses the current location query for an SSO callback. The marker must be present; without a
 * code but with an error parameter the provider rejected the sign-in (access_denied and friends).
 */
export function parseSsoCallback(query: string): SsoCallbackParse {
  const params = new URLSearchParams(query);
  if (params.get('sso') !== 'callback') {
    return { kind: 'none' };
  }
  const error = params.get('error');
  if (error !== null) {
    return { kind: 'provider_error', error, description: params.get('error_description') };
  }
  const code = params.get('code');
  const state = params.get('state');
  if (code === null || code.length === 0 || state === null || state.length === 0) {
    return { kind: 'provider_error', error: 'invalid_callback', description: null };
  }
  return { kind: 'callback', code, state };
}

// --- Token exchange ---------------------------------------------------------------------------

/** Narrowed success of a token endpoint call (RFC 6749 section 5.1 subset we need). */
export interface SsoTokenResponse {
  readonly accessToken: string;
  readonly tokenType: string;
  /** Access-token lifetime in seconds, when the provider sent a parseable one. */
  readonly expiresIn: number | null;
  /** Rotating refresh token; null when the provider answered without one. */
  readonly refreshToken: string | null;
  /** Refresh-token lifetime in seconds (the realm sends refresh_expires_in), when parseable. */
  readonly refreshExpiresIn: number | null;
  readonly scope: string | null;
}

/** Failure of the token exchange, with the text the prejoin notice shows. */
export class SsoExchangeError extends Error {
  /** 'network' (fetch failed), 'oidc_error' (RFC 6749 section 5.2 body) or 'invalid_response'. */
  readonly kind: 'network' | 'oidc_error' | 'invalid_response';
  readonly oidcError: string | null;

  constructor(
    kind: 'network' | 'oidc_error' | 'invalid_response',
    message: string,
    oidcError: string | null = null,
  ) {
    super(message);
    this.name = 'SsoExchangeError';
    this.kind = kind;
    this.oidcError = oidcError;
  }
}

function isFiniteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** Narrows the token endpoint's JSON body; throws SsoExchangeError on any shape violation. */
export function parseTokenResponse(body: unknown): SsoTokenResponse {
  if (!isRecord(body)) {
    throw new SsoExchangeError('invalid_response', 'token response is not a JSON object');
  }
  const accessToken = readNonEmpty(body.access_token);
  if (accessToken === null) {
    throw new SsoExchangeError(
      'invalid_response',
      'token response field "access_token" is missing',
    );
  }
  const expiresIn = isFiniteNumber(body.expires_in) ? body.expires_in : null;
  const refreshExpiresIn = isFiniteNumber(body.refresh_expires_in) ? body.refresh_expires_in : null;
  return {
    accessToken,
    tokenType: readNonEmpty(body.token_type) ?? '',
    expiresIn,
    refreshToken: readNonEmpty(body.refresh_token),
    refreshExpiresIn,
    scope: readNonEmpty(body.scope),
  };
}

/** Inputs of the code-to-token exchange; the redirectUri must equal the authorize request's. */
export interface SsoExchangeInput {
  readonly code: string;
  readonly verifier: string;
  readonly redirectUri: string;
}

/**
 * Exchanges the authorization code for tokens at the realm's token endpoint (fetch POST,
 * form-encoded; CORS is allowed for the SPA origins on the stand). The default scope=openid
 * response carries the refresh token pair; parseTokenResponse narrows it and buildSsoSession
 * turns it into the stored session shape.
 */
export async function exchangeCodeForTokens(
  config: SsoConfig,
  input: SsoExchangeInput,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<SsoTokenResponse> {
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code: input.code,
    code_verifier: input.verifier,
    redirect_uri: input.redirectUri,
  });

  let response: Response;
  try {
    response = await fetchImpl(tokenEndpoint(config), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    });
  } catch {
    throw new SsoExchangeError('network', 'could not reach the sign-in service');
  }

  let parsed: unknown;
  try {
    parsed = (await response.json()) as unknown;
  } catch {
    parsed = null;
  }

  if (!response.ok) {
    if (isRecord(parsed) && readNonEmpty(parsed.error) !== null) {
      const code = parsed.error;
      if (typeof code === 'string') {
        const description = readNonEmpty(parsed.error_description);
        throw new SsoExchangeError(
          'oidc_error',
          description ?? `the sign-in service rejected the request (${code})`,
          code,
        );
      }
    }
    throw new SsoExchangeError(
      'invalid_response',
      `the sign-in service answered with HTTP ${response.status}`,
    );
  }

  return parseTokenResponse(parsed);
}

// --- Access-token payload ---------------------------------------------------------------------

/** Claims the SPA decodes client-side, for display only (the server derives everything else). */
export interface SsoTokenClaims {
  readonly preferredUsername: string | null;
}

/** Common tail of the base64url JWT payload decoders in this module. */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  const segments = token.split('.');
  const payloadSegment = segments[1];
  if (segments.length !== 3 || payloadSegment === undefined) {
    return null;
  }
  const decoded = base64urlDecode(payloadSegment);
  if (decoded === null) {
    return null;
  }
  try {
    const parsed: unknown = JSON.parse(decoded);
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Decodes the JWT payload of the access token without verifying the signature (the control
 * service verifies it on join; the SPA only shows the name). Corrupted input yields null.
 */
export function decodeAccessTokenPayload(token: string): SsoTokenClaims | null {
  const parsed = decodeJwtPayload(token);
  if (parsed === null) {
    return null;
  }
  return { preferredUsername: readNonEmpty(parsed.preferred_username) };
}

/**
 * Expiry (epoch ms) decoded from a JWT's exp claim; null when absent, malformed or in the past
 * of the parse itself. Display/expiry heuristic only - the server verifies the signature.
 */
export function decodeTokenExpiryMs(token: string): number | null {
  const exp = decodeJwtPayload(token)?.exp;
  return typeof exp === 'number' && Number.isFinite(exp) && exp > 0 ? exp * 1000 : null;
}

/**
 * Builds the stored session from a narrowed token response. The access expiry prefers
 * expires_in and falls back to the token's exp claim; with neither the session expires
 * immediately (safe: the renewal paths then treat it as stale instead of over-trusting it).
 */
export function buildSsoSession(tokens: SsoTokenResponse, nowMs: number = Date.now()): SsoSession {
  const fromExpiresIn =
    tokens.expiresIn !== null && tokens.expiresIn > 0 ? nowMs + tokens.expiresIn * 1000 : null;
  const accessExpiresAt = fromExpiresIn ?? decodeTokenExpiryMs(tokens.accessToken) ?? nowMs;
  const session: {
    accessToken: string;
    refreshToken?: string;
    accessExpiresAt: number;
    refreshExpiresAt?: number;
  } = { accessToken: tokens.accessToken, accessExpiresAt };
  if (tokens.refreshToken !== null) {
    session.refreshToken = tokens.refreshToken;
  }
  if (tokens.refreshExpiresIn !== null && tokens.refreshExpiresIn > 0) {
    session.refreshExpiresAt = nowMs + tokens.refreshExpiresIn * 1000;
  }
  return session;
}

// --- Complete sign-in (mount-time callback handler) ---------------------------------------------

/** Outcome of the mount-time callback handling on the prejoin screen. */
export type SsoSignInOutcome =
  | { readonly kind: 'signed_in'; readonly accessToken: string; readonly username: string }
  /** No flow state, or CSRF state mismatch: the flow is stale or foreign - start again. */
  | { readonly kind: 'stale_state' }
  | { readonly kind: 'provider_error'; readonly message: string }
  | { readonly kind: 'exchange_failed'; readonly message: string };

/** Fallback display name when the token carries no preferred_username. */
const FALLBACK_USERNAME = 'SSO user';

/** Display name decoded from the access token; falls back when the claim is absent. */
function usernameFromToken(accessToken: string): string {
  return decodeAccessTokenPayload(accessToken)?.preferredUsername ?? FALLBACK_USERNAME;
}

/**
 * Completes a sign-in from the callback query: validates the CSRF state against the stored
 * flow, exchanges the code, keeps only the access token in sessionStorage and clears the flow.
 * The storage is cleared on every terminal outcome; the query stripping stays the caller's job.
 */
export async function completeSsoSignIn(
  config: SsoConfig,
  callback: { readonly code: string; readonly state: string },
  input: SsoStartInput,
  storage: KeyValueStorage,
  fetchImpl: typeof fetch = globalThis.fetch,
): Promise<SsoSignInOutcome> {
  const flow = loadSsoFlow(storage);
  if (flow === null || flow.state !== callback.state || flow.room !== input.room) {
    clearSsoFlow(storage);
    return { kind: 'stale_state' };
  }
  try {
    const tokens = await exchangeCodeForTokens(
      config,
      {
        code: callback.code,
        verifier: flow.verifier,
        redirectUri: `${input.origin}/r/${input.room}?${SSO_CALLBACK_MARKER}`,
      },
      fetchImpl,
    );
    clearSsoFlow(storage);
    const session = buildSsoSession(tokens);
    saveSsoSession(storage, session);
    const username = usernameFromToken(session.accessToken);
    return { kind: 'signed_in', accessToken: session.accessToken, username };
  } catch (err: unknown) {
    clearSsoFlow(storage);
    if (err instanceof SsoExchangeError && err.kind === 'oidc_error') {
      return { kind: 'provider_error', message: err.message };
    }
    return {
      kind: 'exchange_failed',
      message:
        err instanceof Error && err.message.length > 0 ? err.message : 'token exchange failed',
    };
  }
}

// --- Refresh grant (RFC 6749 section 6) ---------------------------------------------------------

/** Failure of a refresh attempt that is not an RFC 6749 invalid_grant answer. */
export class SsoRefreshError extends Error {
  /** 'no_session' (nothing stored), 'network' (fetch failed) or 'invalid_response' (bad body). */
  readonly kind: 'no_session' | 'network' | 'invalid_response';

  constructor(kind: 'no_session' | 'network' | 'invalid_response', message: string) {
    super(message);
    this.name = 'SsoRefreshError';
    this.kind = kind;
  }
}

/** Outcome of one refresh attempt against the realm's token endpoint. */
export type SsoRefreshOutcome =
  /** The pair rotated (or another tab had just rotated it); `accessToken` is the fresh bearer. */
  | { readonly kind: 'refreshed'; readonly accessToken: string }
  /** The provider rejected the grant: the stored session was cleared - re-sign-in required. */
  | { readonly kind: 'invalid_grant' };

/**
 * Structural subset of the Web Locks manager the refresh serialization needs (the full DOM
 * `LockManager` satisfies it); tests inject fakes, `null` disables cross-tab locking.
 */
export interface SsoLockManager {
  request<T>(name: string, callback: () => Promise<T>): Promise<T>;
}

/** Web Locks name serializing the refresh-token rotation across the origin's tabs. */
export const SSO_REFRESH_LOCK_NAME = 'vc.sso.refresh';

/**
 * Cross-tab mutex for browsers without Web Locks (Firefox): a localStorage bread-alley keyed
 * per tab. Acquire writes a claim and re-reads it (localStorage read-after-write is atomic per
 * origin, so a same-tick write from another tab cannot be missed); a claim older than the
 * stale window is taken over (a crashed tab never releases). Release removes own claims only.
 */
const REFRESH_CLAIM_KEY = 'vc.sso.refreshClaim';
const REFRESH_CLAIM_STALE_MS = 10_000;
const refreshClaimTabId: string = (() => {
  try {
    return crypto.randomUUID();
  } catch {
    return `tab-${Math.random().toString(36).slice(2)}`;
  }
})();

function claimRefreshMutex(): boolean {
  try {
    const raw = localStorage.getItem(REFRESH_CLAIM_KEY);
    if (raw !== null) {
      const claim = JSON.parse(raw) as { tabId?: unknown; ts?: unknown };
      const fresh = typeof claim.ts === 'number' && Date.now() - claim.ts < REFRESH_CLAIM_STALE_MS;
      if (claim.tabId !== refreshClaimTabId && fresh) {
        return false;
      }
    }
    localStorage.setItem(
      REFRESH_CLAIM_KEY,
      JSON.stringify({ tabId: refreshClaimTabId, ts: Date.now() }),
    );
    // Re-read: a same-tick write from another tab must be detected (its tabId would have
    // overwritten ours). An unreadable round-trip (stubbed storage in tests) counts as ours.
    const roundTrip = localStorage.getItem(REFRESH_CLAIM_KEY) ?? '';
    return roundTrip.length === 0 || roundTrip.includes(`"${refreshClaimTabId}"`);
  } catch {
    return false;
  }
}

function releaseRefreshMutex(): void {
  try {
    const raw = localStorage.getItem(REFRESH_CLAIM_KEY);
    if (raw !== null && raw.includes(`"${refreshClaimTabId}"`)) {
      localStorage.removeItem(REFRESH_CLAIM_KEY);
    }
  } catch {
    /* storage unavailable: nothing to release */
  }
}

/** Polls the bread-alley until the mutex frees up (bounded), or gives up. */
async function acquireRefreshMutex(): Promise<boolean> {
  const deadline = Date.now() + REFRESH_CLAIM_STALE_MS;
  for (;;) {
    if (claimRefreshMutex()) {
      return true;
    }
    if (Date.now() >= deadline) {
      return false;
    }
    await new Promise((resolve) => {
      setTimeout(resolve, 100);
    });
  }
}

function defaultLockManager(): SsoLockManager | null {
  if (typeof navigator === 'undefined') {
    return null;
  }
  const locks = navigator.locks;
  if (typeof locks?.request !== 'function') {
    return null;
  }
  return {
    request: <T>(name: string, callback: () => Promise<T>): Promise<T> =>
      locks.request(name, callback),
  };
}

/** Options of {@link refreshSsoSession}. */
export interface SsoRefreshOptions {
  /**
   * Lock manager override: undefined autodetects `navigator.locks`, null runs the legacy
   * lock-free path (the in-lock re-read below still guards the common cross-tab race).
   */
  readonly locks?: SsoLockManager | null;
}

/**
 * Process-wide single-flight: one refresh per storage object at a time. The scheduler tick,
 * the 401 heal, the pre-flight and any second tab landing in this context all await the same
 * promise, so a near-expiry burst collapses into exactly one token POST.
 */
const inFlightRefreshes = new WeakMap<KeyValueStorage, Promise<SsoRefreshOutcome>>();

/**
 * Renews the session with the RFC 6749 refresh grant (no PKCE needed for refresh_token).
 * The realm enforces rotation (revokeRefreshToken=true, maxReuse=0): a successful response
 * always carries a fresh refresh_token and the stored pair is overwritten together - keeping
 * the consumed token would only guarantee the next refresh fails. A 400/401 answer clears the
 * session (the caller shows the expired screen); a network or malformed-body failure keeps the
 * session and throws SsoRefreshError so the caller can retry later.
 *
 * The POST is guarded on three layers. (1) Single-flight: concurrent callers share one
 * in-flight promise. (2) Cross-tab: with Web Locks available the POST runs inside the
 * `vc.sso.refresh` lock, so two tabs can never POST the same (single-use) token in parallel.
 * (3) Re-read inside the critical section: if another tab rotated the shared pair after this
 * caller captured its token, the POST is skipped while the fresh access token is still valid
 * (the caller just gets it), and otherwise re-targeted onto the CURRENT pair - re-POSTing the
 * captured token would answer invalid_grant and revoke the whole chain.
 */
export function refreshSsoSession(
  config: SsoConfig,
  storage: KeyValueStorage,
  fetchImpl: typeof fetch = globalThis.fetch,
  nowMs: number = Date.now(),
  options: SsoRefreshOptions = {},
): Promise<SsoRefreshOutcome> {
  const running = inFlightRefreshes.get(storage);
  if (running !== undefined) {
    return running;
  }
  const run = doRefreshSsoSession(config, storage, fetchImpl, nowMs, options).finally(() => {
    inFlightRefreshes.delete(storage);
  });
  inFlightRefreshes.set(storage, run);
  return run;
}

async function doRefreshSsoSession(
  config: SsoConfig,
  storage: KeyValueStorage,
  fetchImpl: typeof fetch,
  nowMs: number,
  options: SsoRefreshOptions,
): Promise<SsoRefreshOutcome> {
  const captured = loadSsoSession(storage);
  const capturedRefreshToken = captured?.refreshToken;
  if (
    captured === null ||
    capturedRefreshToken === undefined ||
    capturedRefreshToken.length === 0
  ) {
    throw new SsoRefreshError('no_session', 'no refresh token is stored for this tab');
  }

  const postRefreshGrant = async (refreshToken: string): Promise<SsoRefreshOutcome> => {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    });

    let response: Response;
    try {
      response = await fetchImpl(tokenEndpoint(config), {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // Keep the stored pair: the grant was not consumed, a later tick can retry.
      throw new SsoRefreshError('network', 'could not reach the sign-in service');
    }

    let parsed: unknown = null;
    try {
      parsed = (await response.json()) as unknown;
    } catch {
      parsed = null;
    }

    if (!response.ok) {
      if (response.status === 400 || response.status === 401) {
        // invalid_grant: this token is expired, or it lost a double-POST race against another
        // tab (rotation already happened past it). Before logging out, adopt a fresher pair
        // another tab rotated into storage - re-logging out a user whose access token is
        // actually alive is exactly what we are guarding against.
        const current = loadSsoSession(storage);
        if (
          current !== null &&
          current.refreshToken !== undefined &&
          current.refreshToken !== capturedRefreshToken &&
          current.accessExpiresAt > nowMs
        ) {
          return { kind: 'refreshed', accessToken: current.accessToken };
        }
        clearSsoSession(storage);
        return { kind: 'invalid_grant' };
      }
      throw new SsoRefreshError(
        'invalid_response',
        `the sign-in service answered with HTTP ${response.status}`,
      );
    }

    let tokens: SsoTokenResponse;
    try {
      tokens = parseTokenResponse(parsed);
    } catch {
      throw new SsoRefreshError('invalid_response', 'refresh response is malformed');
    }
    const rotated = buildSsoSession(tokens, nowMs);
    saveSsoSession(storage, rotated);
    return { kind: 'refreshed', accessToken: rotated.accessToken };
  };

  // The critical section: at most one rotation POST per origin at a time, over the freshest
  // pair storage holds when the lock is acquired.
  const insideLock = async (): Promise<SsoRefreshOutcome> => {
    const current = loadSsoSession(storage);
    const currentRefreshToken = current?.refreshToken;
    if (
      current !== null &&
      currentRefreshToken !== undefined &&
      currentRefreshToken.length > 0 &&
      currentRefreshToken !== capturedRefreshToken
    ) {
      // Another tab already rotated: hand out its access token while it is still fresh ...
      if (current.accessExpiresAt > nowMs) {
        return { kind: 'refreshed', accessToken: current.accessToken };
      }
      // ... and only when it is expired renew again, with the CURRENT (live) pair.
      return postRefreshGrant(currentRefreshToken);
    }
    return postRefreshGrant(capturedRefreshToken);
  };

  const locks = options.locks !== undefined ? options.locks : defaultLockManager();
  if (locks === null) {
    // No Web Locks (Firefox): serialize with the localStorage bread-alley instead. A bounded
    // wait keeps a wedged claim from hanging the refresh; on give-up the POST runs anyway and
    // the invalid_grant adoption below keeps a lost race from logging the user out.
    return acquireRefreshMutex().then((acquired) =>
      insideLock().finally(() => {
        if (acquired) {
          releaseRefreshMutex();
        }
      }),
    );
  }
  return locks.request(SSO_REFRESH_LOCK_NAME, insideLock);
}

// --- Auto-refresh scheduler ---------------------------------------------------------------------

/**
 * Subscribes to cross-tab rotations of the shared session: the `storage` event fires in every
 * OTHER tab when one tab overwrites `vc.ssoSession` (a `null` key means a storage clear).
 * Returns the unsubscribe function.
 */
export function onSessionRotated(callback: () => void): () => void {
  if (typeof window === 'undefined') {
    return (): void => undefined;
  }
  const handler = (event: StorageEvent): void => {
    if (event.key === SSO_SESSION_STORAGE_KEY || event.key === null) {
      callback();
    }
  };
  window.addEventListener('storage', handler);
  return (): void => window.removeEventListener('storage', handler);
}

/** True when the stored session can and should be refreshed right now. */
export function sessionDueForRefresh(
  session: SsoSession | null,
  nowMs: number,
  refreshWindowMs: number = 60_000,
): boolean {
  if (session === null || session.refreshToken === undefined) {
    return false;
  }
  // A dead refresh token cannot renew anything; the 401-recovery path surfaces the expiry.
  if (session.refreshExpiresAt !== undefined && session.refreshExpiresAt <= nowMs) {
    return false;
  }
  return session.accessExpiresAt - nowMs < refreshWindowMs;
}

/** Injectable browser surfaces of the scheduler, so tests run without real timers. */
export interface SsoAutoRefreshOptions {
  readonly config: SsoConfig;
  readonly storage: KeyValueStorage;
  readonly fetchImpl?: typeof fetch;
  /** Refresh while less than this remains on the access token (default 60 s). */
  readonly refreshWindowMs?: number;
  /** Check period (default 30 s). */
  readonly intervalMs?: number;
  readonly nowMs?: () => number;
  /** Notified after every successful rotation (callers sync their React state here). */
  readonly onRefreshed?: (accessToken: string) => void;
  /** Called when the grant was rejected and the session was cleared. */
  readonly onInvalidGrant?: () => void;
  /** Visibility probe; defaults to document.visibilityState ('visible' when unavailable). */
  readonly visibility?: () => string;
  /** Timer seam; defaults to setInterval. Returns the cancel function. */
  readonly schedule?: (callback: () => void, ms: number) => () => void;
  /** Visibility-change subscription; defaults to document.addEventListener. */
  readonly onVisibilityChange?: (callback: () => void) => () => void;
  /** Cross-tab rotation subscription; defaults to {@link onSessionRotated} (storage events). */
  readonly onSessionRotated?: (callback: () => void) => () => void;
}

/**
 * Starts the tab-level auto-refresh loop: every intervalMs it renews the stored session when
 * the access token expires within refreshWindowMs, and every return to a visible tab is
 * checked immediately (background tabs throttle timers, so the visibility edge is what saves
 * a session after a long hidden period), and a rotation another tab just wrote to the shared
 * session is picked up immediately too. Refreshes hit Keycloak roughly every 4 minutes with
 * the stand TTLs, which also keeps the realm's 10-minute SSO idle session alive. The loop
 * only acts when a refreshable session exists and the tab is visible; failures to reach the
 * provider keep the session and retry on a later tick. Returns the stop function.
 */
export function startSsoAutoRefresh(options: SsoAutoRefreshOptions): () => void {
  const intervalMs = options.intervalMs ?? 30_000;
  const refreshWindowMs = options.refreshWindowMs ?? 60_000;
  const nowMs = options.nowMs ?? Date.now;
  const visibility =
    options.visibility ??
    (typeof document === 'undefined'
      ? (): string => 'visible'
      : (): string => document.visibilityState);
  const schedule =
    options.schedule ??
    ((callback: () => void, ms: number): (() => void) => {
      const id = setInterval(callback, ms);
      return () => clearInterval(id);
    });
  const onVisibilityChange =
    options.onVisibilityChange ??
    ((callback: () => void): (() => void) => {
      if (typeof document === 'undefined') {
        return (): void => undefined;
      }
      document.addEventListener('visibilitychange', callback);
      return () => document.removeEventListener('visibilitychange', callback);
    });
  const onRotated =
    options.onSessionRotated ??
    ((callback: () => void): (() => void) => onSessionRotated(callback));

  let inFlight = false;
  const check = (): void => {
    if (inFlight || visibility() !== 'visible') {
      return;
    }
    const session = loadSsoSession(options.storage);
    if (!sessionDueForRefresh(session, nowMs(), refreshWindowMs)) {
      return;
    }
    inFlight = true;
    void refreshSsoSession(options.config, options.storage, options.fetchImpl, nowMs())
      .then((outcome) => {
        if (outcome.kind === 'refreshed') {
          options.onRefreshed?.(outcome.accessToken);
        } else {
          options.onInvalidGrant?.();
        }
      })
      .catch(() => {
        // network / invalid_response / no_session: keep the session, retry on a later tick.
      })
      .finally(() => {
        inFlight = false;
      });
  };

  const stopInterval = schedule(check, intervalMs);
  const unsubscribeVisibility = onVisibilityChange(() => {
    if (visibility() === 'visible') {
      check();
    }
  });
  // Another tab rotated the shared pair: re-check immediately (the fresh session is usually
  // no longer due, which is exactly how the second tab avoids a second POST of one token).
  const unsubscribeRotation = onRotated(check);
  return () => {
    stopInterval();
    unsubscribeVisibility();
    unsubscribeRotation();
  };
}

// --- API-call recovery --------------------------------------------------------------------------

/** Options of {@link callWithSsoRecovery}. */
export interface SsoRecoveryOptions {
  readonly config: SsoConfig;
  /** Null storage disables recovery entirely (the call runs once, unrenewed). */
  readonly storage: KeyValueStorage | null;
  readonly fetchImpl?: typeof fetch;
  /** Classifies the error as "the bearer token was rejected" (HTTP 401 / sso_invalid). */
  readonly rejected: (error: unknown) => boolean;
  /** Pre-flight refresh window (default 60 s): renew before the first attempt when close. */
  readonly preRefreshWindowMs?: number;
  readonly nowMs?: () => number;
  /** Notified with the rotated token so callers can sync their state. */
  readonly onRefreshed?: (accessToken: string) => void;
}

/**
 * Runs an authenticated call with session-recovery semantics:
 *
 * 1. Pre-flight (join path): when the stored access token expires within
 *    preRefreshWindowMs, refresh once and run the call with the rotated token. A failed
 *    pre-flight is not fatal - the call itself decides through `rejected`.
 * 2. Reactive: when the call fails and `rejected` says the token was refused, refresh once
 *    and retry with the new token. A second rejection surfaces as-is; an invalid_grant
 *    surfaces the ORIGINAL rejection (the session is gone, the caller shows the expired
 *    screen); a network/malformed refresh surfaces the retryable SsoRefreshError.
 */
export async function callWithSsoRecovery<T>(
  token: string,
  call: (accessToken: string) => Promise<T>,
  options: SsoRecoveryOptions,
): Promise<T> {
  const nowMs = options.nowMs ?? Date.now;
  const renew = (storage: KeyValueStorage): Promise<SsoRefreshOutcome> =>
    refreshSsoSession(options.config, storage, options.fetchImpl, nowMs());
  const noteRefreshed = (accessToken: string): void => {
    options.onRefreshed?.(accessToken);
  };

  let current = token;
  const storage = options.storage;
  if (storage !== null) {
    const preWindow = options.preRefreshWindowMs ?? 60_000;
    const session = loadSsoSession(storage);
    if (sessionDueForRefresh(session, nowMs(), preWindow)) {
      // A failed pre-flight is never fatal: the call below proceeds with the current token
      // and its own rejection classification stays the single source of truth.
      const pre = await renew(storage).catch((): SsoRefreshOutcome | null => null);
      if (pre?.kind === 'refreshed') {
        current = pre.accessToken;
        noteRefreshed(current);
      }
    }
  }

  try {
    return await call(current);
  } catch (error: unknown) {
    if (!options.rejected(error) || storage === null) {
      throw error;
    }
    let outcome: SsoRefreshOutcome;
    try {
      outcome = await renew(storage);
    } catch (refreshError: unknown) {
      if (refreshError instanceof SsoRefreshError && refreshError.kind === 'no_session') {
        // Nothing to renew with: the original rejection is the honest answer.
        throw error;
      }
      throw refreshError;
    }
    if (outcome.kind === 'invalid_grant') {
      throw error;
    }
    current = outcome.accessToken;
    noteRefreshed(current);
    return await call(current);
  }
}
